import React, { useState, useEffect } from 'react';
import { DetailElement, DetailLayout } from '../../../components/DetailLayout';
import Toggle from '../../../components/Toggle/Toggle';
import Button from '../../../components/Button/Button';
import {
  TipCalculationRow,
  TipValueInputs,
  CustomTipSelector,
  TipToggleMessage,
  ForcedTipSection,
  DEFAULT_SERVICE_CHARGE_TIP_PLACEHOLDERS,
  TIP_MESSAGE_MAX_LENGTH
} from '../../../components/TipConfiguration';
import { ILocationGratuity } from '@ready/dashboardv2api.contracts';
import styles from './TipConfigurationPanel.module.scss';

export interface TipConfigurationPanelProps {
  tipConfig: ILocationGratuity;
  loading: boolean;
  onUpdate: (config: ILocationGratuity) => void;
}

const TipConfigurationPanel: React.FC<TipConfigurationPanelProps> = ({ tipConfig, loading, onUpdate }) => {
  const [localConfig, setLocalConfig] = useState<ILocationGratuity>(tipConfig);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setLocalConfig(tipConfig);
    setHasChanges(false);
  }, [tipConfig]);

  const handleChange = (field: keyof ILocationGratuity, value: any) => {
    const updatedConfig = { ...localConfig, [field]: value };
    setLocalConfig(updatedConfig);
    setHasChanges(true);
  };



  const handleSave = () => {
    onUpdate(localConfig);
    setHasChanges(false);
  };

  const handleReset = () => {
    setLocalConfig(tipConfig);
    setHasChanges(false);
  };



  return (
    <div className={styles.tipConfigurationPanel}>
      <DetailLayout>
        <DetailElement label="Allow Tips">
          <Toggle
            checked={localConfig.allowTips}
            onChange={(checked: boolean) => handleChange('allowTips', checked)}
            loading={loading}
            additionalStyles={styles.leftAlignedToggle}
          />
        </DetailElement>

        {localConfig.allowTips && (
          <>
            <DetailElement label="Default Tip Values">
              <TipValueInputs
                values={localConfig.defaultTips}
                onChange={(index, value) => {
                  const updatedTips = [...localConfig.defaultTips];
                  updatedTips[index] = value;
                  handleChange('defaultTips', updatedTips);
                }}
                loading={loading}
                keyPrefix="default-tip"
              />
            </DetailElement>

            <DetailElement
              label="Default Tip Values for Service Charges"
              labelTooltip="Tip options used when a service charge is present on the check."
            >
              <TipValueInputs
                values={localConfig.defaultTipsForServiceCharges}
                onChange={(index, value) => {
                  const updatedTips = [...localConfig.defaultTipsForServiceCharges];
                  updatedTips[index] = value;
                  handleChange('defaultTipsForServiceCharges', updatedTips);
                }}
                placeholders={DEFAULT_SERVICE_CHARGE_TIP_PLACEHOLDERS}
                loading={loading}
                keyPrefix="service-tip"
              />
            </DetailElement>

            <DetailElement label='Default "Custom Tip" Selection'>
              <CustomTipSelector
                value={localConfig.defaultCustomTipType}
                onChange={(value) => handleChange('defaultCustomTipType', value)}
                loading={loading}
              />
            </DetailElement>

            <DetailElement label="Tip Calculation">
              <div className={styles.calculationContainer}>
                <div className={styles.calculationHeader}>Calculate tips based on the total amount:</div>
                <TipCalculationRow
                  value={localConfig.preDiscountTip}
                  onChange={(value: boolean) => handleChange('preDiscountTip', value)}
                  label="Check discounts"
                  loading={loading}
                />
                <TipCalculationRow
                  value={localConfig.preItemDiscountTip}
                  onChange={(value: boolean) => handleChange('preItemDiscountTip', value)}
                  label="Item discounts"
                  loading={loading}
                />
                <TipCalculationRow
                  value={localConfig.preTaxTip}
                  onChange={(value: boolean) => handleChange('preTaxTip', value)}
                  label="Tax"
                  loading={loading}
                />
                <TipCalculationRow
                  value={localConfig.preNonReadyPaymentsTip}
                  onChange={(value: boolean) => handleChange('preNonReadyPaymentsTip', value)}
                  label="Non-Ready payments"
                  loading={loading}
                />
              </div>
            </DetailElement>

            <DetailElement label="Display Custom Message">
              <TipToggleMessage
                checked={localConfig.showBottomText}
                onToggleChange={(checked) => handleChange('showBottomText', checked)}
                message={localConfig.bottomText}
                onMessageChange={(message) => handleChange('bottomText', message)}
                loading={loading}
                maxLength={TIP_MESSAGE_MAX_LENGTH}
                placeholder=""
              />
            </DetailElement>

            <DetailElement
              label="Forced Tip"
              labelTooltip="When enabled, guests will see a message about automatic gratuity instead of tip options"
            >
              <ForcedTipSection
                enabled={localConfig.forcedTipEnabled ?? false}
                onEnabledChange={(enabled: boolean) => handleChange('forcedTipEnabled', enabled)}
                percentage={localConfig.forcedTipPercentage ?? 0}
                onPercentageChange={(percentage: number) => handleChange('forcedTipPercentage', percentage)}
                loading={loading}
                placeholder="0"
              />
            </DetailElement>
          </>
        )}

        {hasChanges && (
          <DetailElement label="">
            <div className={styles.actionButtons}>
              <Button
                variant="primary"
                label="Save Changes"
                onClick={handleSave}
                loading={loading}
                disabled={loading}
              />
              <Button
                variant="secondary"
                label="Reset"
                onClick={handleReset}
                disabled={loading}
                additionalStyles={styles.resetButton}
              />
            </div>
          </DetailElement>
        )}
      </DetailLayout>
    </div>
  );
};

export default TipConfigurationPanel;